import {React, classNames, InfiniteScroll, _, useLocalStore, useEffect, useAsObservableSource, observer} from '@api'
import {UNASSIGNED} from '@api/graphql'
import {FILTER_UNASSIGNED_ID} from '@modules/catalog'
import {CatalogItemsDataVirtualRow} from '@modules/catalog/sidebar/CatalogItemsList/CatalogItemsDataVirtualRow'
import {LeftSidebarHeader} from '@modules/catalog/sidebar/LeftSidebar'
import {LoadingIndicator} from '@modules/common'
import {CatalogItemsListItemContextMenu, IContextMenuItems} from '@modules/contextMenus'
import {ExceptionDetails} from '@modules/errors'
import {StoresInstanceContext, useStores, useVipLocalStore, VipLocalStore} from '@modules/VipLocalStore'
import {DragDropEventHandlers, site, IDataMapConfig, isTagField, StoresInstance} from '@stores'
import {ListPreviewCard, DataMapper, ListInfoCard, CollapsibleStateful} from 'centric-ui-components'
import {action, computed, reaction, toJS} from 'mobx'
import {Fragment, useContext} from 'react'
import * as css from './CatalogItemsList.css'
import {AddCatalogItemSidebar, CatalogItemsListChip, CatalogItemsListStore, CatalogItemsListProps, CatalogItemsListStoreContext, CatalogItemsListToolbar, useCatalogItemsListStore, CatalogItemsToggleListItem} from '.'

export const CatalogItemsList = observer<CatalogItemsListProps>(function CatalogItemsList({className, ...props}) {
  const parentStores = useStores(),
    obsProps = useAsObservableSource({...props, parentStores, visible: props.visible}),
    store = useVipLocalStore((stores) => {
      return new CatalogItemsListStore(stores, obsProps)
    }),
    {
      strings, loadMore, count, lastCount, virtualRowCount, loadingMore, hasMore, renderer, libraryCountSearchTerms, props: {visible},
      library, catalogField, error, libraryQueryClause,
      dataMap, seasonalData, catalogItemsListData, assignableField, flexLayout: {isMaximized},
      catalog: {es}, stores: {sidebar: {left: {libraryView}}}, countByValue
    } = store

  useEffect(() => store.registerEffects(), [])

  // Reset the store if the user has changed libraries - but don't reset it if they change to null and back
  useEffect(() => {
    (library || catalogField) && store.reset()
  }, [libraryCountSearchTerms, library, catalogField])

  useEffect(() => {
    store.lastCount = count ?? lastCount
  }, [count])

  useEffect(() => {
    store.resetPaging()
  }, [library && libraryQueryClause])

  useEffect(() => {
    library?.refetchOnHierarchyChange && store.reset()
  }, [library?.refetchOnHierarchyChange && es.catalogSeasonalTerms])

  //console.log({pageIndex, searchText: store.sidebar.searchText, seasonalData, count, loadingMore, showLoader: count === null || (pageIndex == 1 && loadingMore)})
  return (
    <CatalogItemsListStoreContext.Provider value={store}>
      <StoresInstanceContext.Provider value={store.overrideStores}>
        {store.overrideStores &&
         <div className={classNames(className, css.root)}
             data-fake-header={!props.library && !props.catalogField}
             data-type={'CatalogItemsList'}
             data-count={count ?? lastCount}
             data-library={library?.id}
             data-field={assignableField?.field}
             data-loading-more={loadingMore}
             data-has-more={hasMore}
             data-view={libraryView}
             data-popover={props.isPopover}
             onContextMenu={store.onContextMenu}
        >
           {!isMaximized && <LeftSidebarHeader/>}

          <CatalogItemsListToolbar/>
          <div className={css.list} data-renderer={renderer}>
            {
              error ? <ExceptionDetails className={css.error} error={error}/> :
              library?.seasonalGrouping ?
              <Fragment>
                <CollapsibleStateful title={`Assigned (${_.size(seasonalData)})`} classes={{list: css.collapsibleList, wrapperInner: css.collapsibleWrapperInner}}>
                  {_.size(seasonalData) > 0 &&
				   <div className={css.infinite}>
                     {seasonalData.map((r, i) =>
                       <CatalogDataItem_Mapped data={r} dataMap={dataMap} key={i}>
                         <CatalogDataItem data={seasonalData}/>
                       </CatalogDataItem_Mapped>)
                     }
                     {assignableField?.def?.type != 'Boolean' && !_.isBoolean(assignableField?.showAsLibrary) && assignableField?.showAsLibrary?.unassign !== false && <CatalogDataItem data={{_id: UNASSIGNED, title: strings.unassigned, count: countByValue[UNASSIGNED]}} className={css.unassign}/>}
				   </div>}
                </CollapsibleStateful>
                <CollapsibleStateful title={`Available (${count - _.size(seasonalData)})`} classes={{list: css.collapsibleList, wrapperInner: css.collapsibleWrapperInner}}>
                  <InfiniteScroll
                    className={css.infinite}
                    data-type='InfiniteScroll'
                    data-loading={loadingMore}
                    pageStart={1}
                    loadMore={loadMore}
                    hasMore={hasMore}
                    useWindow={false}
                    loader={<div className={css.loader} key={0}>Loading More...</div>}
                  >
                    {(
                      <Fragment>
                        {_.range(0, 1).map(i => <CatalogItemsDataVirtualRow key={i} index={virtualRowCount}/>)}
                        {visible && _.size(catalogItemsListData) == 0 ?
                         loadingMore ? <LoadingIndicator className={css.loadingIndicator}/>
                                     : <NoResultsFound/> : null}
                      </Fragment>)}
                  </InfiniteScroll>
                </CollapsibleStateful>
              </Fragment> : <CatalogItemsListInfiniteScroller/>
            }
          </div>
          {(library?.editable || isTagField(assignableField)) && <AddCatalogItemSidebar/>}
        </div>}
      </StoresInstanceContext.Provider>
    </CatalogItemsListStoreContext.Provider>
  )
})
CatalogItemsList.defaultProps = {
  titleStyle: 'name'
}

export const CatalogItemsListInfiniteScroller = observer(function CatalogItemsListInfiniteScroller() {
  const {renderer, loadingMore, loadMore, hasMore, catalogItemsListData, library, virtualRowCount} = useCatalogItemsListStore()
  return (
    <InfiniteScroll
      className={classNames(css.infinite, {[css.noPadding]: renderer != 'chip' && _.size(catalogItemsListData) == 0 && !loadingMore})}
      data-type='InfiniteScroll'
      data-loading={loadingMore}
      pageStart={1}
      //ref={inputRef}
      loadMore={loadMore}
      hasMore={hasMore}
      useWindow={false}
      loader={virtualRowCount > 1 ? <div className={css.loader} key={0}>{library ? `Loading More...` : null}</div> : null}
    >
      <CatalogItemsListInfiniteScrollerVirtualRows/>
    </InfiniteScroll>
  )
})

const NoResultsFound = observer(function NoResultsFound() {
  const {strings, loading, loadingMore, searching, catalogItemsListData, searchText} = useCatalogItemsListStore()
  return ((!loading && !loadingMore) || searching) && _.size(catalogItemsListData) === 0
         ? <div className={css.noResults}>{strings.noResultsFoundFor(searchText)}</div> //<ProductLandingComponent searchText={searchText}/>
         : null
})

export const CatalogItemsListInfiniteScrollerVirtualRows = observer(function CatalogItemsListInfiniteScrollerVirtualRows() {
  const {strings, renderer, loadingMore, showUnassignChip, props: {visible}, virtualRowCount, key, countByValue} = useCatalogItemsListStore()

  return (
    <Fragment>
      {_.range(0, virtualRowCount).map(i => <CatalogItemsDataVirtualRow key={`${key}_${i}`} index={i}/>)}

      {showUnassignChip && <CatalogDataItem data={{_id: UNASSIGNED, title: strings.unassigned, count: countByValue[UNASSIGNED] /* ${label}`*/}} className={css.unassign}/>}

      {visible && virtualRowCount > 1 && loadingMore && renderer != 'chip' && <LoadingIndicator className={css.loadingIndicator}/>}

      <NoResultsFound/>
    </Fragment>
  )
})

export class CatalogDataItemUIStore extends VipLocalStore implements IContextMenuItems {
  constructor(stores: StoresInstance, private props: { data }, public store: CatalogItemsListStore) {
    super(stores)
  }

  get data() { return this.props.data}

  get field() { return this.store.assignableField}

  get library() { return this.store.library }

  get workspace() { return this.store.libraryWorkspace}

  get prototypicalCardSize() {
    return {width: 300, height: 100}
  }

  get visibleCardFields() {
    const {store: {dataMap}, catalog: {_fieldByName}} = this
    return _.values(dataMap).map(f => _fieldByName[f]).filter(_.identity)
  }

  registerEffects = () => {
    const dispose = [
    ]

    const {id, catalog: {componentStores: {itemsListItem}}} = this

    itemsListItem[id] = this

    return () => {
      dispose.forEach(f => f())
      delete itemsListItem[id]
    }
  }

  @computed get id() { return this.catalog.idFor(this.data?.item) ?? this.catalog.idFor(this.data)}

  @computed get isDragOverSelf() {
    return this.id === this.dragDrop?.dragOverItem?.id
  }

  @computed get somethingDroppedOverMe() {
    return this.ddHandler.state.status === 'loading'
  }

  @computed get showSuccess() {
    return this.ddHandler.state.status === 'success'
  }

  @computed get ddHandler() {
    const {field, library, data, catalog, dragDrop: {ASSIGN_FIELD, ASSIGN_LIBRARY}} = this,
      type = field?.field || library?.index,
      sectionConfig = catalog.sectionConfigFor(field?.section)

    const r = new DragDropEventHandlers(catalog, {
      id: data._id,
      where: data.sectionValue ? {[`${sectionConfig?.path}.${sectionConfig?.viewOptions.field}`]: data.sectionValue} : undefined,
      section: data.sectionValue,
      dragType: type,
      target: type,
      label: data.title ?? data.name ?? data.label ?? (field ? catalog.labelForField(field) : catalog.labelForLibrary({id: library?.id})),
      meta: field?.field ? ASSIGN_FIELD : library?.index ? ASSIGN_LIBRARY : null,
      targetExtra: {data}
    })

    //console.log(r)
    return r
  }

  @action onClickChip = id => e => {
    const {store: {getActiveItem}, data} = this
    getActiveItem(data)
  }

  onContextMenu = e => {
    this.contextMenu.onComponentContextMenu(e, this)
  }

  get contextMenuItems() {
    return <CatalogDataItemUIStoreContext.Provider value={this}>
      <CatalogItemsListItemContextMenu/>
    </CatalogDataItemUIStoreContext.Provider>
  }

  @computed get sectionConfig() {
    const {field, catalog: {sectionConfigFor}} = this
    return sectionConfigFor(field?.section)
  }

  @computed get count() {
    const {data, catalog: {filter}, field, sectionConfig} = this

    if (_.isNumber(data.count)) return data.count

    if (!field) return null

    const filterGroup = filter.groupFor(field.field)

    if (filterGroup) {
      if (sectionConfig?.viewOptions?.field && data.sectionValue) {
        if (!filter.hasLoaded) return null
        // todo move this stuff into our own count query
        const buckets = filter.data && filter?.data[field.field] && filter?.data[field.field]?.filterAggs?.items?.buckets

        if (!_.isEmpty(buckets)) {
          const match = buckets.find(b => b.key == data._id) // do not make === we are comparing stuff like false to 0 from ES
          if (match) {
            return match[data.sectionValue]?.doc_count
          }
        }

        return null
      }

      let groupedByValue = filterGroup?.groupedByValue ?? {},
        {_id} = data,
        count = Number(groupedByValue[_id == UNASSIGNED ? FILTER_UNASSIGNED_ID : _id]?.count || 0)

      if (_id === UNASSIGNED) {
        count += Number(groupedByValue['']?.count || 0)
      }

      // console.log(toJS({data, groupedByValue, field, count, _id}, {recurseEverything:true}))
      return count
    }

    return null
  }
}

export const CatalogDataItemUIStoreContext = React.createContext<CatalogDataItemUIStore>(undefined)
export const useCatalogDataItemUIStore = () => useContext(CatalogDataItemUIStoreContext)

export const CatalogDataItem = observer<{ style?, data, className?, dataMap?: IDataMapConfig }>(function CatalogDataItem({style, data, className, dataMap, ...props}) {
  const parentStore = useCatalogItemsListStore(),
    {onClick, library, label, assignableField: field, renderer, isEditMode, onDelete, getActiveItem, sidebar: {libraryView}} = parentStore,
    obsProps = useAsObservableSource({data, field}),
    store = useVipLocalStore((ws) => new CatalogDataItemUIStore(ws, obsProps, parentStore)),
    {stores, count, ddHandler, ddHandler: {draggable}, somethingDroppedOverMe, onClickChip, onContextMenu, id} = store,
    showImage = library !== null || field?.esIndex != null,
    {sidebar: {searchText}, searchWords} = useCatalogItemsListStore()

  useEffect(() => store.registerEffects(),[])

  return <CatalogDataItemUIStoreContext.Provider value={store}>
    <div
      className={css.dataItem}
      draggable={draggable}
      data-id={id}
      data-field={field?.field}
      data-library={library?.id}
      data-drag-type={ddHandler.def.dragType}
      data-renderer={renderer}
      data-is-dragging={ddHandler.isDragging}
      onContextMenu={onContextMenu}
      {...props}
      {...ddHandler.events}
    >
      {/* Uncomment if D&D of catalog item -> attribute card is desired */}
      {/*<DragEventPlaceholders dropType={ddHandler.def.target} id={ddHandler.def.id} /> */}
      {renderer == 'chip'
       ? <CatalogItemsListChip className={className}
                               data={data}
                               count={count}
                               searchWords={searchWords}
                               draggable={draggable}
                               onClickChip={isEditMode && data._id !== UNASSIGNED ? onClickChip : undefined}
                               onDelete={() => isEditMode && data._id !== UNASSIGNED ? onDelete(data) : undefined}/>
       : renderer == 'toggleList'
         ? <CatalogItemsToggleListItem data={data}/>
         : renderer == 'card'
           ? (libraryView == 'card'
              ? <ListPreviewCard
                stores={stores}
                className={css.listPreviewCard} classes={{list: css.previewList}}
                onClick={onClick(data._id)}
                dataMap={dataMap}
                showImage={showImage}
                canShowAssignedToBadge
                avatar={showImage ? _.toUpper(label ? label[0] : '?') : undefined}
                library={library}
                field={field}
                data={data}
                draggable={draggable}
                searchText={searchText}
                hideTooltips
                showTooltip={false}
                loading={somethingDroppedOverMe}
                showHex={showImage}/>
              : <ListInfoCard
                showImage={showImage}
                showHex={showImage}
                isContent
                draggable={draggable}
                cardSize={'xlarge'}
                searchText={searchText}
                data={data}
                isSelected={false}
                onClick={onClick(data._id)}
                classes={{
                  large_card: css.large_card
                }}/>)
           : <div>Unknown renderer {library?.renderer}</div>
      }
    </div>
  </CatalogDataItemUIStoreContext.Provider>
})

export const CatalogDataItem_Mapped = observer<{ dataMap, data, children?: React.ReactNode }, any>(function CatalogDataItem_Mapped({dataMap, data, children}, ref) {
  const {strings} = useStores()

  return (
    <DataMapper config={dataMap} data={data} ref={ref} nullValue={strings.emptyLabel}>
      {children}
    </DataMapper>
  )
}, {forwardRef: true})


// WEBPACK FOOTER //
// ./src/modules/catalog/sidebar/CatalogItemsList/CatalogItemsList.tsx